import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { ChatStatus } from "ai";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  useAgnoChat,
  type AgnoChatMessage,
  type UseAgnoChatReturn,
} from "@/hooks/use-agno-chat";
import { BACKEND_RECONNECTED_EVENT } from "@/lib/backend-connectivity";
import { useOptionalNotificationContext } from "@/components/notification-provider";
import {
  isCanonical,
  mergeMessages,
  selectOldestCanonicalId,
  selectNewestCanonicalId,
  selectSecondNewestCanonicalId,
} from "@/lib/message-store";
import { mapSessionMessage } from "@/lib/session-message-mapper";
import type {
  SessionMessageListResponse,
  SessionMessageResponse,
} from "@/lib/session";
import { cacheScope, clearCacheOnAuthFailure, invalidateCache, messageCacheEnabled, readCache, writeCache } from "@/lib/ui-cache";
import { messageTailPreview } from "@/lib/ui-cache-shapes";
import { useCacheScope } from "@/hooks/use-cache-scope";
import {
  pushConversationDestination,
  type ConversationTarget,
} from "@/lib/conversation-target";
import { extractConversationHeadings } from "@/lib/conversation-headings";
import {
  fetchNavigationContentChunk,
  fetchNavigationWindow,
  fetchNavigationWindowPage,
  isOversizedNavigationMessage,
  probeNavigationCapabilities,
} from "@/api/conversation-navigation";
import {
  activateMessageWindow,
  createMessageWindowStore,
  mergeAdjacentWindow,
  selectLatestTail,
  updateIsolatedTail,
  type MessageWindowCoverage,
} from "@/lib/message-window-store";

/** Number of messages to load per page (initial load and each "load more"). */
const PAGE_SIZE = 30;
const STANDALONE_NOTIFICATIONS = {
  notificationState: new Map(),
  markSessionRead: (_sessionId: string) => {},
  setRunStatus: (_sessionId: string, _status: string) => {},
  registerSeenRun: (_sessionId: string, _runId: string) => {},
};
const POLL_INTERVAL_MS = 15_000;
const POLL_PAGE_SIZE = PAGE_SIZE;
const TERMINAL_UNREAD_RETRY_DELAYS_MS = [1_000, 2_000];

/**
 * Recovery polling serializes its tail merges with prepends. A submitted turn
 * or live SSE does not, so preserve the historical guard for those transports.
 */
function hasUnserializedMessageStream(
  status: ChatStatus,
  isPolling: boolean,
): boolean {
  return status === "submitted" || (status === "streaming" && !isPolling);
}

type QueuedMessageMutationKind = "poll-tail" | "prepend";

interface QueuedMessageMutation {
  generation: number;
  revision: number;
  kind: QueuedMessageMutationKind;
  /** A prepend stays locked through its follow-up cleanup render. */
  awaitingPrependCleanup?: boolean;
  resolve: () => void;
}

export interface UseSessionMessageFeedOptions {
  bootstrap?: import("@/api/conversation-bootstrap").ConversationBootstrap | null;
  bootstrapPending?: boolean;
  /** Permit a transcript to render outside the chat notification shell. */
  readOnly?: boolean;
  sessionId: string;
  agentId: string | null;
  fetchWithAuth: (url: string, init: RequestInit) => Promise<Response>;
  /** See useAgnoChat: skip the mount-time /runs check for a newly-created session. */
  isNewSession?: boolean;
  /** Keeps the parent sidebar/session list in sync with real status transitions. */
  onStatusChange?: (status: ChatStatus, prevStatus: ChatStatus) => void;
  /** Optional consumer notification when the feed becomes non-empty or empty. */
  onHasMessagesChange?: (hasMessages: boolean) => void;
  onToolCompleted?: (event: { toolName: string; result: unknown }) => void;
  /** Override idle history refresh cadence for an actively followed child. */
  pollIntervalMs?: number;
  /** True only after a real user event in this conversation during this view. */
  hasUserInteracted?: boolean;
}

export interface LoadMoreMessagesOptions {
  /**
   * Manual recovery requests may replace a stale automatic prepend attempt.
   * The active request is aborted before this page is fetched.
   */
  force?: boolean;
}

export interface UseSessionMessageFeedReturn {
  messages: AgnoChatMessage[];
  sendMessage: UseAgnoChatReturn["sendMessage"];
  status: ChatStatus;
  /** Recovery polling has a serialized tail-merge path and can paginate safely. */
  isPolling: UseAgnoChatReturn["isPolling"];
  stop: UseAgnoChatReturn["stop"];
  recoverableFailedRun: UseAgnoChatReturn["recoverableFailedRun"];
  continueFromFailedRun: UseAgnoChatReturn["continueFromFailedRun"];
  isUploading: boolean;
  isTranscribing: boolean;
  continueRun: UseAgnoChatReturn["continueRun"];
  hasMoreMessages: boolean;
  /** Snapshot count from the latest limit-only history response. */
  totalMessages: number | null;
  isLoadingMore: boolean;
  olderPageError: string | null;
  newerPageError: string | null;
  loadMoreMessages: (options?: LoadMoreMessagesOptions) => Promise<void>;
  /** True for the render transaction that commits an older page. */
  isPrepending: boolean;
  /** Monotonic token for a committed older-page prepend. */
  prependRevision: number;
  hasLoadedMessages: boolean;
  messageLoadError: string | null;
  handleRetryMessageLoad: () => void;
  loadNavigationTarget: (
    target: ConversationTarget,
    signal: AbortSignal,
  ) => Promise<NavigationTargetLoadResult>;
  isHistoricalWindow: boolean;
  hasNewerWindowActivity: boolean;
  jumpToLatest: () => void;
  navigationCoverage: MessageWindowCoverage | null;
  navigationContentMessageId: string | null;
  navigationContentError: boolean;
  continueNavigationContent: (messageId: string) => Promise<void>;
  cancelNavigationContent: () => void;
  isLoadingNewerNavigation: boolean;
  loadNewerNavigationMessages: () => Promise<void>;
}

export type NavigationTargetLoadResult =
  | { state: "loaded"; messageId: string }
  | { state: "unsupported" }
  | { state: "missing" }
  | { state: "denied" }
  | { state: "error" };

function mapWindowCoverage(coverage: {
  first_message_id: string;
  last_message_id: string;
  has_older: boolean;
  has_newer: boolean;
  before_cursor: string | null;
  after_cursor: string | null;
  starts_at_turn_boundary: boolean;
  ends_at_turn_boundary: boolean;
  partial_turn: boolean;
}): MessageWindowCoverage {
  return {
    firstMessageId: coverage.first_message_id,
    lastMessageId: coverage.last_message_id,
    hasOlder: coverage.has_older,
    hasNewer: coverage.has_newer,
    beforeCursor: coverage.before_cursor,
    afterCursor: coverage.after_cursor,
    startsAtTurnBoundary: coverage.starts_at_turn_boundary,
    endsAtTurnBoundary: coverage.ends_at_turn_boundary,
    partialTurn: coverage.partial_turn,
  };
}

/**
 * Owns the server-backed message feed for a conversation.
 *
 * useAgnoChat owns live SSE/recovery updates; this hook layers historical
 * loading, backward pagination, reconciliation, and external-update refreshes
 * over that same canonical message state.
 */
export function useSessionMessageFeed({
  bootstrap,
  bootstrapPending = false,
  readOnly = false,
  sessionId,
  agentId,
  fetchWithAuth,
  isNewSession,
  onStatusChange,
  onHasMessagesChange,
  onToolCompleted,
  pollIntervalMs = POLL_INTERVAL_MS,
  hasUserInteracted = false,
}: UseSessionMessageFeedOptions): UseSessionMessageFeedReturn {
  const verifiedScope = useCacheScope();
  const { t } = useTranslation();

  // Polling and an older-page prepend both mutate the one shared message list.
  // Hold this queue until React's layout phase has committed each mutation, not
  // merely until its setState call has been queued. That guarantees Virtua sees
  // a prepend as a pure start mutation even while a long-lived recovery poll is
  // refreshing the tail. Live SSE remains outside this queue by design and
  // continues to use the existing conservative pagination guard.
  const [messageMutationRevision, setMessageMutationRevision] = useState(0);
  const messageMutationRevisionRef = useRef(0);
  const messageMutationGenerationRef = useRef(0);
  const messageMutationQueueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingMessageMutationRef = useRef<QueuedMessageMutation | null>(null);
  const queueMessageMutation = useCallback(async (
    kind: QueuedMessageMutationKind,
    commit: (revision: number) => boolean | void,
  ) => {
    const generation = messageMutationGenerationRef.current;
    const previous = messageMutationQueueRef.current;
    const task = previous.then(() => new Promise<void>((resolve) => {
      if (messageMutationGenerationRef.current !== generation) {
        resolve();
        return;
      }

      const revision = ++messageMutationRevisionRef.current;
      pendingMessageMutationRef.current = {
        generation,
        revision,
        kind,
        resolve,
      };
      if (commit(revision) === false) {
        pendingMessageMutationRef.current = null;
        resolve();
        return;
      }
      // This explicit revision update guarantees a commit acknowledgement even
      // when mergeMessages determines that a polling response is a no-op.
      setMessageMutationRevision(revision);
    }));
    // A cancelled session must not poison later session work with a rejected
    // queue tail. Individual callers still receive the original rejection.
    messageMutationQueueRef.current = task.catch(() => undefined);
    await task;
  }, []);
  const queueRecoveryMessageMerge = useCallback((commit: () => void) => (
    queueMessageMutation("poll-tail", () => {
      commit();
      return true;
    })
  ), [queueMessageMutation]);
  const historicalWindowRef = useRef(false);
  const messageWindowStoreRef = useRef(createMessageWindowStore());
  const navigationRequestGenerationRef = useRef(0);
  const capabilityCacheRef = useRef<{
    sessionId: string;
    expiresAt: number;
    result: Awaited<ReturnType<typeof probeNavigationCapabilities>>;
  } | null>(null);
  const [isHistoricalWindow, setIsHistoricalWindow] = useState(false);
  const [hasNewerWindowActivity, setHasNewerWindowActivity] = useState(false);
  const [navigationCoverage, setNavigationCoverage] = useState<MessageWindowCoverage | null>(null);
  const [navigationContentMessageId, setNavigationContentMessageId] = useState<string | null>(null);
  const [navigationContentError, setNavigationContentError] = useState(false);
  const navigationContentControllerRef = useRef<AbortController | null>(null);
  const navigationNewerControllerRef = useRef<AbortController | null>(null);
  const [isLoadingNewerNavigation, setIsLoadingNewerNavigation] = useState(false);
  const [olderPageError, setOlderPageError] = useState<string | null>(null);
  const [newerPageError, setNewerPageError] = useState<string | null>(null);
  const reportPageError = useCallback((direction: "older" | "newer", error: unknown) => {
    const status = typeof error === "object" && error !== null && "status" in error
      ? Number(error.status) : undefined;
    const message = t([404, 405, 501].includes(status ?? 0)
      ? "navigation.pageUnsupported"
      : direction === "older" ? "navigation.olderPageError" : "navigation.newerPageError");
    (direction === "older" ? setOlderPageError : setNewerPageError)(message);
    console.error(`[ui] Failed to load ${direction} messages:`, error);
    toast.error(message, { id: `conversation-page-${sessionId}-${direction}` });
  }, [sessionId, t]);
  const restoreLatestBeforeSendRef = useRef<() => void>(() => {});
  const handleHistoricalTailMessages = useCallback((incoming: AgnoChatMessage[]) => {
    const previousActivity = messageWindowStoreRef.current.newTailActivity;
    messageWindowStoreRef.current = updateIsolatedTail(
      messageWindowStoreRef.current,
      incoming,
    );
    const next = messageWindowStoreRef.current.newTailActivity;
    if (next !== previousActivity) setHasNewerWindowActivity(next);
  }, []);

  const {
    messages,
    setMessages,
    sendMessage,
    status,
    stop,
    resume,
    isUploading,
    isTranscribing,
    recoverableFailedRun,
    continueFromFailedRun,
    continueRun,
    pausedForHitl,
    isPolling,
  } = useAgnoChat({
    sessionId,
    agentId,
    fetchWithAuth,
    isNewSession,
    bootstrapRuns: bootstrap?.runs,
    bootstrapPending,
    onToolCompleted,
    queueRecoveryMessageMerge,
    historicalWindowRef,
    onHistoricalTailMessages: handleHistoricalTailMessages,
    beforeSendFromHistoricalWindow: () => restoreLatestBeforeSendRef.current(),
  });

  useEffect(() => {
    setMessages([]);
  }, [verifiedScope, setMessages]);

  // Pagination state is deliberately separate from the message list. Cursors
  // themselves are derived from the canonical rows in messagesRef below.
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [totalMessages, setTotalMessages] = useState<number | null>(null);
  // The periodic poll checks this before enqueueing a state update. Returning
  // the old value from a functional updater can still commit this hook.
  const totalMessagesRef = useRef(totalMessages);
  totalMessagesRef.current = totalMessages;
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Keep the prepend transaction open through the render that introduces the
  // older page. The scroll controller consumes this domain signal to preserve
  // the reader's position; this feed never touches virtualizer APIs.
  const [isPrepending, setIsPrepending] = useState(false);
  const prependingRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const isPrependingRef = useRef(isPrepending);
  const isPollingRef = useRef(isPolling);
  const activePrependControllerRef = useRef<AbortController | null>(null);
  const prependRequestGenerationRef = useRef(0);
  const prependRevisionRef = useRef(0);
  const [prependRevision, setPrependRevision] = useState(0);
  const pendingPrependCommitRef = useRef<{
    sessionId: string;
    revision: number;
    expectedMessageId: string | null;
    messageMutationRevision: number;
  } | null>(null);
  // Avoid notifying the provider repeatedly when React replays an effect or a
  // post-stream reconciliation returns the same canonical row twice.
  const registeredSeenRunIdsRef = useRef(new Set<string>());
  // True once the initial request has resolved, whether it succeeded or not.
  const [hasLoadedMessages, setHasLoadedMessages] = useState(false);
  const [loadedSessionId, setLoadedSessionId] = useState<string | null>(null);
  const [messageLoadError, setMessageLoadError] = useState<string | null>(null);
  const [messageLoadRetryNonce, setMessageLoadRetryNonce] = useState(0);
  // Prevent a re-fired first-page request from restoring `hasMore` after the
  // user has already paged backward.
  const hasLoadedOlderRef = useRef(false);

  // This is assigned during render so an async callback cannot briefly see
  // the previous session between a prop change and the next effect flush.
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  isPrependingRef.current = isPrepending;
  isLoadingMoreRef.current = isLoadingMore;
  isPollingRef.current = isPolling;

  // Pagination cursors are derived from the single message source of truth.
  // The ref lets asynchronous callbacks see its latest value without
  // re-subscribing on every streamed token.
  const messagesRef = useRef<typeof messages>(messages);
  messagesRef.current = messages;
  useEffect(() => {
    if (historicalWindowRef.current) return;
    messageWindowStoreRef.current = {
      ...messageWindowStoreRef.current,
      tail: messages,
    };
  }, [messages]);

  // Async listeners need the current status without capturing a stale closure.
  const statusRef = useRef(status);
  statusRef.current = status;

  // Keep resume out of the visibility listener dependencies: its identity can
  // change on session changes, while the listener only needs its latest value.
  const resumeRef = useRef(resume);
  resumeRef.current = resume;

  const notifications = useOptionalNotificationContext();
  if (!notifications && !readOnly) {
    throw new Error("Interactive session feeds require a NotificationProvider");
  }
  const {
    notificationState,
    markSessionRead,
    setRunStatus,
    registerSeenRun,
  } = notifications ?? STANDALONE_NOTIFICATIONS;
  const markInboxReadBySession = notifications?.inbox.markReadBySession;
  const mapMessage = useCallback(mapSessionMessage, []);
  const registerNewestCanonicalRun = useCallback((
    capturedSessionId: string,
    sourceMessages: AgnoChatMessage[],
  ) => {
    for (let index = sourceMessages.length - 1; index >= 0; index -= 1) {
      const message = sourceMessages[index];
      if (!isCanonical(message)) continue;
      const runId = message.metadata?.runId;
      if (typeof runId !== "string" || runId.length === 0) continue;
      if (registeredSeenRunIdsRef.current.has(runId)) return;

      registeredSeenRunIdsRef.current.add(runId);
      registerSeenRun(capturedSessionId, runId);
      return;
    }
  }, [registerSeenRun]);

  useEffect(() => {
    onHasMessagesChange?.(messages.length > 0);
  }, [messages.length, onHasMessagesChange]);

  // Keep sidebar run indicators current and reconcile optimistic SSE rows once
  // a completed stream has persisted its canonical DB rows.
  const prevStatusRef = useRef(status);
  useEffect(() => {
    const capturedSessionId = sessionId;
    const controller = new AbortController();
    let cancelled = false;
    const ownsSession = () => (
      !cancelled
      && !controller.signal.aborted
      && sessionIdRef.current === capturedSessionId
    );
    const prev = prevStatusRef.current;
    const wasInFlight = prev === "submitted" || prev === "streaming";
    if (!ownsSession()) return () => {
      cancelled = true;
      controller.abort();
    };

    if (status === "submitted" || status === "streaming") {
      setRunStatus(capturedSessionId, "running");
    } else if (status === "ready" && wasInFlight) {
      // A HITL pause is not a completed stream. Keep its client-side approval
      // card instead of reconciling it away as an optimistic row.
      if (pausedForHitl.current) {
        setRunStatus(capturedSessionId, "paused");
        console.log("[ui] Post-stream reconciliation skipped — HITL pause active");
        prevStatusRef.current = status;
        onStatusChange?.(status, prev);
        return () => {
          cancelled = true;
          controller.abort();
        };
      }

      setRunStatus(capturedSessionId, "completed");
      // The response was already visible via SSE. Remember its run id before
      // the asynchronous durable notification row can reach the inbox poller.
      registerNewestCanonicalRun(capturedSessionId, messagesRef.current);
      const newestCursor = selectNewestCanonicalId(messagesRef.current);
      // A terminal run can finalize its newest canonical row in place. Reading
      // after that exact id would never return the changed row, so retain one
      // predecessor when possible; with only one canonical row, reread the
      // small latest page instead. This is also the tail reconciliation used
      // when checkAndPoll observes a terminal run before its final poll tick.
      const reconciliationCursor = selectSecondNewestCanonicalId(messagesRef.current);
      const reconcileUrl = newestCursor && reconciliationCursor
        ? `/api/sessions/${capturedSessionId}/messages?after=${encodeURIComponent(reconciliationCursor)}`
        : `/api/sessions/${capturedSessionId}/messages?limit=50&complete_turns=true`;
      fetchWithAuth(reconcileUrl, { headers: {}, signal: controller.signal })
        .then((res) => {
          if (!res.ok) return null;
          return res.json() as Promise<SessionMessageListResponse>;
        })
        .then((data) => {
          if (!data) return;
          const incoming = data.messages.map(mapMessage);
          // The canonical response can land just after the stream's terminal
          // event. Register it too, including if navigation happened while
          // this harmless reconciliation request was completing.
          registerNewestCanonicalRun(capturedSessionId, incoming);
          if (!ownsSession() || prependingRef.current) return;
          if (typeof data.total === "number") {
            setTotalMessages(data.total);
          }
          // Only remove optimistic rows after canonical replacements arrive.
          if (incoming.length === 0) return;
          console.log("[ui] Post-stream reconciliation: merging", incoming.length, "DB messages for session", capturedSessionId);
          setMessages((previous) => (
            ownsSession() && !prependingRef.current && !isPrependingRef.current
              ? mergeMessages(previous, incoming, { dropOptimistic: true })
              : previous
          ));
        })
        .catch((error) => {
          if (controller.signal.aborted || (error instanceof DOMException && error.name === "AbortError")) return;
          if (ownsSession()) {
            console.error("[ui] Post-stream reconciliation failed:", error);
          }
        });
    } else if (status === "error" && wasInFlight) {
      setRunStatus(capturedSessionId, "failed");
      registerNewestCanonicalRun(capturedSessionId, messagesRef.current);
      // Error streams do not use the normal optimistic-row reconciliation,
      // but their terminal canonical rows can still arrive just after the SSE
      // error event. Read them only to capture the run id; leave the error UI
      // and its recovery behavior untouched.
      fetchWithAuth(
        `/api/sessions/${capturedSessionId}/messages?limit=50&complete_turns=true`,
        { headers: {}, signal: controller.signal },
      )
        .then((res) => (res.ok ? res.json() as Promise<SessionMessageListResponse> : null))
        .then((data) => {
          if (!data) return;
          registerNewestCanonicalRun(capturedSessionId, data.messages.map(mapMessage));
        })
        .catch((error) => {
          if (controller.signal.aborted || (error instanceof DOMException && error.name === "AbortError")) return;
          if (ownsSession()) {
            console.error("[ui] Failed-run seen-id lookup failed:", error);
          }
        });
    }
    prevStatusRef.current = status;
    if (ownsSession()) onStatusChange?.(status, prev);

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    status,
    sessionId,
    setRunStatus,
    onStatusChange,
    fetchWithAuth,
    mapMessage,
    setMessages,
    pausedForHitl,
    registerNewestCanonicalRun,
  ]);

  // Reset feed-specific state for each session. useAgnoChat resets its own
  // live state independently.
  useEffect(() => {
    activePrependControllerRef.current?.abort();
    activePrependControllerRef.current = null;
    pendingPrependCommitRef.current = null;
    messageMutationGenerationRef.current += 1;
    const pendingMutation = pendingMessageMutationRef.current;
    pendingMessageMutationRef.current = null;
    pendingMutation?.resolve();
    messageMutationQueueRef.current = Promise.resolve();
    setHasMoreMessages(false);
    setTotalMessages(null);
    setIsLoadingMore(false);
    prependingRef.current = false;
    setIsPrepending(false);
    isPrependingRef.current = false;
    isLoadingMoreRef.current = false;
    prependRequestGenerationRef.current += 1;
    hasLoadedOlderRef.current = false;
    prependRevisionRef.current = 0;
    setPrependRevision(0);
    setHasLoadedMessages(false);
    setLoadedSessionId(null);
    setMessageLoadError(null);
    historicalWindowRef.current = false;
    messageWindowStoreRef.current = createMessageWindowStore();
    navigationRequestGenerationRef.current += 1;
    capabilityCacheRef.current = null;
    navigationContentControllerRef.current?.abort();
    navigationContentControllerRef.current = null;
    navigationNewerControllerRef.current?.abort();
    navigationNewerControllerRef.current = null;
    setIsHistoricalWindow(false);
    setHasNewerWindowActivity(false);
    setNavigationCoverage(null);
    setNavigationContentMessageId(null);
    setNavigationContentError(false);
    setIsLoadingNewerNavigation(false);
    setOlderPageError(null);
    setNewerPageError(null);
    prevStatusRef.current = statusRef.current;
    return () => {
      activePrependControllerRef.current?.abort();
      navigationNewerControllerRef.current?.abort();
    };
  }, [sessionId]);

  // The prepend flag is an operation token, not a timer. It is set in the same
  // batch as the older-page merge and cleared in the layout phase after that
  // render commits, so downstream position preservation sees one transaction.
  useLayoutEffect(() => {
    const pending = pendingPrependCommitRef.current;
    if (
      !pending
      || pending.sessionId !== sessionId
      || pending.revision !== prependRevision
      || sessionIdRef.current !== sessionId
      || (pending.expectedMessageId !== null
        && !messages.some((message) => message.id === pending.expectedMessageId))
    ) {
      return;
    }

    pendingPrependCommitRef.current = null;
    isPrependingRef.current = false;
    setIsPrepending(false);
    const queuedMutation = pendingMessageMutationRef.current;
    if (
      queuedMutation?.kind === "prepend"
      && queuedMutation.revision === pending.messageMutationRevision
    ) {
      queuedMutation.awaitingPrependCleanup = true;
    }
  }, [messages, prependRevision, sessionId]);

  // A queued poll-tail merge is safe once its render has committed. A queued
  // prepend needs one more render: the first is where Virtua applies `shift`,
  // and this cleanup render drops the transaction flag before the next tail
  // merge is allowed to begin.
  useLayoutEffect(() => {
    const pending = pendingMessageMutationRef.current;
    if (
      !pending
      || pending.revision !== messageMutationRevision
      || pending.generation !== messageMutationGenerationRef.current
    ) {
      return;
    }

    if (
      pending.kind === "poll-tail"
      || (pending.awaitingPrependCleanup && !isPrepending)
    ) {
      pendingMessageMutationRef.current = null;
      pending.resolve();
    }
  }, [isPrepending, messageMutationRevision]);

  // Initial history page. Abort and cancelled guards make StrictMode and a
  // changing authenticated-fetch wrapper harmless.
  useEffect(() => {
    const capturedSessionId = sessionId;
    const capturedScope = cacheScope();
    const controller = new AbortController();
    let cancelled = false;
    let live = false;

    const cachedTail = capturedScope && !isNewSession && messageCacheEnabled()
      ? readCache<SessionMessageListResponse & { generated_at?: string }>("messageTails", "tail", capturedSessionId)
      : Promise.resolve(null);
    if (capturedScope && !isNewSession && messageCacheEnabled()) {
      void cachedTail.then((snapshot) => {
        if (cancelled || live || cacheScope() !== capturedScope || sessionIdRef.current !== capturedSessionId || !snapshot?.messages) return;
        const safe = messageTailPreview(snapshot.messages).map(mapMessage);
        if (!safe.length) return;
        setMessages((previous) => mergeMessages(previous, safe));
        setHasMoreMessages(snapshot.has_more === true);
        setTotalMessages(typeof snapshot.total === "number" ? snapshot.total : null);
      });
    }

    if (bootstrapPending) return () => { cancelled = true; controller.abort(); };

    const initialRequest: Promise<SessionMessageListResponse> = bootstrap?.session.id === capturedSessionId
      ? Promise.resolve(bootstrap.messages)
      : fetchWithAuth(
      `/api/sessions/${capturedSessionId}/messages?limit=${PAGE_SIZE}&complete_turns=true`,
      { headers: {}, signal: controller.signal },
    )
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            clearCacheOnAuthFailure();
            setMessages([]);
            void invalidateCache("messageTails", "tail", capturedSessionId);
          }
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json() as Promise<SessionMessageListResponse>;
      });
    initialRequest.then(async (data) => {
        const snapshot = await cachedTail;
        if (cancelled || sessionIdRef.current !== capturedSessionId || capturedScope !== cacheScope()) return;
        live = true;
        // A cache written after this read snapshot (for example by an active
        // conversation in another tab) remains the more recent tail.
        if (bootstrap && snapshot?.generated_at && snapshot.generated_at > bootstrap.generated_at) {
          setLoadedSessionId(capturedSessionId);
          return;
        }
        if (messageCacheEnabled()) void writeCache("messageTails", "tail", capturedSessionId, { messages: messageTailPreview(data.messages), has_more: data.has_more === true, total: data.total ?? null, generated_at: bootstrap?.generated_at ?? new Date().toISOString() });
        const loaded = data.messages.map(mapMessage);
        console.log("[ui] Loaded", loaded.length, "messages for session", capturedSessionId, "has_more:", data.has_more);
        if (typeof data.total === "number") {
          setTotalMessages(data.total);
        }
        setMessages((previous) => (
          !cancelled && sessionIdRef.current === capturedSessionId
            ? mergeMessages(previous, loaded)
            : previous
        ));
        if (!hasLoadedOlderRef.current) {
          setHasMoreMessages(data.has_more ?? false);
        }
        setMessageLoadError(null);
        setLoadedSessionId(capturedSessionId);
      })
      .catch((error) => {
        if (controller.signal.aborted || (error instanceof DOMException && error.name === "AbortError")) return;
        console.error("[ui] Failed to load historical messages for session", capturedSessionId, ":", error);
        if (!cancelled && sessionIdRef.current === capturedSessionId) {
          const errorDetail = error instanceof Error ? error.message.trim() : "";
          const errorHint = t("common:errors.messageLoadErrorHint");
          setMessageLoadError(errorDetail ? `${errorHint} (${errorDetail})` : errorHint);
        }
      })
      .finally(() => {
        if (!cancelled && sessionIdRef.current === capturedSessionId) {
          setHasLoadedMessages(true);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [sessionId, messageLoadRetryNonce, fetchWithAuth, setMessages, mapMessage, t, verifiedScope, isNewSession, bootstrap, bootstrapPending]);

  const handleRetryMessageLoad = useCallback(() => {
    setMessageLoadError(null);
    setHasLoadedMessages(false);
    setLoadedSessionId(null);
    setMessageLoadRetryNonce((nonce) => nonce + 1);
  }, []);

  // Inbox-only notifications also wait for a visible, successfully loaded
  // transcript before the engagement signal acknowledges them.
  useEffect(() => {
    if (!hasUserInteracted || loadedSessionId !== sessionId || messages.length === 0) return;
    if (notificationState.get(sessionId)?.hasUnread) return;
    if (!notifications?.inbox.notifications?.some((row) => (
      row.sessionId === sessionId && row.readAt === null && row.dismissedAt === null
    ))) return;
    void markInboxReadBySession?.(sessionId);
  }, [hasUserInteracted, loadedSessionId, sessionId, messages.length, notificationState, notifications?.inbox.notifications, markInboxReadBySession]);

  // A terminal notification means another actor may have persisted new rows.
  // Never reconcile during live SSE: sorting then would strand the optimistic
  // assistant placeholder and freeze token updates.
  useEffect(() => {
    const capturedSessionId = sessionId;
    const controller = new AbortController();
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const ownsSession = () => (
      !cancelled
      && !controller.signal.aborted
      && sessionIdRef.current === capturedSessionId
    );
    const sessionNotif = notificationState.get(sessionId);
    if (!sessionNotif?.hasUnread) return () => {
      cancelled = true;
      controller.abort();
    };

    // Selecting a thread deliberately does not acknowledge it. Keep the
    // terminal-status fetch below intact, but only start it after the reader
    // has scrolled, clicked in the transcript, or typed in the composer.
    if (!hasUserInteracted) return () => {
      cancelled = true;
      controller.abort();
    };

    if (loadedSessionId !== sessionId || messagesRef.current.length === 0) return () => {
      cancelled = true;
      controller.abort();
    };

    const terminalStatuses = new Set(["completed", "failed", "cancelled"]);
    if (sessionNotif.runStatus == null || !terminalStatuses.has(sessionNotif.runStatus)) return () => {
      cancelled = true;
      controller.abort();
    };

    if (status === "submitted" || status === "streaming") {
      return () => {
        cancelled = true;
        controller.abort();
      };
    }

    const cursor = historicalWindowRef.current
      ? null
      : selectNewestCanonicalId(messagesRef.current);
    if (!cursor && !historicalWindowRef.current) {
      return () => {
        cancelled = true;
        controller.abort();
      };
    }

    const retry = (attempt: number) => {
      if (attempt >= TERMINAL_UNREAD_RETRY_DELAYS_MS.length || !ownsSession()) return;
      retryTimer = setTimeout(() => {
        retryTimer = undefined;
        void refetch(attempt + 1);
      }, TERMINAL_UNREAD_RETRY_DELAYS_MS[attempt]);
    };
    const refetch = async (attempt: number) => {
      if (!ownsSession() || !notificationState.get(capturedSessionId)?.hasUnread) return;
      if (statusRef.current === "submitted" || statusRef.current === "streaming") return;
      try {
        const res = await fetchWithAuth(
          historicalWindowRef.current
            ? `/api/sessions/${capturedSessionId}/messages?limit=${POLL_PAGE_SIZE}&complete_turns=true`
            : `/api/sessions/${capturedSessionId}/messages?after=${encodeURIComponent(cursor!)}`,
          { headers: {}, signal: controller.signal },
        );
        if (!res.ok) throw new Error(`Notification refetch returned HTTP ${res.status}`);
        const data = await res.json() as SessionMessageListResponse;
        if (!ownsSession() || prependingRef.current || isPrependingRef.current) return;
        const incoming = data.messages.map(mapMessage);
        if (incoming.length > 0) {
          console.log("[ui] Notification: merging", incoming.length, "new messages for session", capturedSessionId);
          if (historicalWindowRef.current) {
            handleHistoricalTailMessages(incoming);
          } else {
            setMessages((previous) => {
              if (
                !ownsSession()
                || prependingRef.current
                || isPrependingRef.current
              ) return previous;
              const hasOptimistic = previous.some((message) => !isCanonical(message));
              return mergeMessages(
                previous,
                incoming,
                hasOptimistic ? { dropOptimistic: true } : undefined,
              );
            });
          }
        }
        if (ownsSession()) {
          markSessionRead(capturedSessionId);
          void markInboxReadBySession?.(capturedSessionId);
        }
      } catch (error) {
        if (controller.signal.aborted || (error instanceof DOMException && error.name === "AbortError")) return;
        if (ownsSession()) {
          console.error("[ui] Notification refetch failed:", error);
          retry(attempt);
        }
      }
    };
    void refetch(0);

    return () => {
      cancelled = true;
      controller.abort();
      if (retryTimer !== undefined) clearTimeout(retryTimer);
    };
  }, [
    notificationState,
    sessionId,
    fetchWithAuth,
    mapMessage,
    setMessages,
    markSessionRead,
    markInboxReadBySession,
    status,
    hasUserInteracted,
    loadedSessionId,
    handleHistoricalTailMessages,
  ]);

  // When focus returns, resume an interrupted stream or refresh the current
  // tail. Reconnects use the same incremental refresh without restarting the
  // initial history/cache hydration effect or disturbing older loaded pages.
  useEffect(() => {
    const capturedSessionId = sessionId;
    const controller = new AbortController();
    let cancelled = false;
    const ownsSession = () => (
      !cancelled
      && !controller.signal.aborted
      && sessionIdRef.current === capturedSessionId
    );

    function refreshTail() {
      const cursor = selectSecondNewestCanonicalId(messagesRef.current)
        ?? selectNewestCanonicalId(messagesRef.current);
      if (!cursor) {
        // A session with no canonical cursor needs its first page again. This
        // also recovers an initial request that failed during the outage.
        setMessageLoadRetryNonce((nonce) => nonce + 1);
        return;
      }

      fetchWithAuth(
        historicalWindowRef.current
          ? `/api/sessions/${capturedSessionId}/messages?limit=${POLL_PAGE_SIZE}&complete_turns=true`
          : `/api/sessions/${capturedSessionId}/messages?after=${encodeURIComponent(cursor)}`,
        { headers: {}, signal: controller.signal },
      )
        .then((res) => {
          if (!res.ok) return null;
          return res.json() as Promise<SessionMessageListResponse>;
        })
        .then((data) => {
          if (!data || !ownsSession() || prependingRef.current) return;
          setMessageLoadError(null);
          const incoming = data.messages.map(mapMessage);
          if (incoming.length === 0) return;
          console.log("[ui] Tail refresh: merging", incoming.length, "new messages for session", capturedSessionId);
          if (historicalWindowRef.current) {
            handleHistoricalTailMessages(incoming);
            return;
          }
          setMessages((previous) => {
            if (
              !ownsSession()
              || prependingRef.current
              || isPrependingRef.current
            ) return previous;
            const hasOptimistic = previous.some((message) => !isCanonical(message));
            return mergeMessages(
              previous,
              incoming,
              hasOptimistic ? { dropOptimistic: true } : undefined,
            );
          });
        })
        .catch((error) => {
          if (controller.signal.aborted || (error instanceof DOMException && error.name === "AbortError")) return;
          if (ownsSession()) console.error("[ui] Tail refresh failed:", error);
        });
    }

    function handleVisibility() {
      if (document.visibilityState !== "visible") return;
      if (statusRef.current === "submitted" || statusRef.current === "streaming") {
        if (ownsSession()) resumeRef.current?.();
        return;
      }
      if (selectNewestCanonicalId(messagesRef.current)) refreshTail();
    }

    function handleReconnect() {
      // useAgnoChat already checks active runs on reconnect. Keep this path to
      // idle history so it cannot race an SSE or recovery-poll merge.
      if (statusRef.current === "submitted" || statusRef.current === "streaming") return;
      refreshTail();
    }

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener(BACKEND_RECONNECTED_EVENT, handleReconnect);
    return () => {
      cancelled = true;
      controller.abort();
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener(BACKEND_RECONNECTED_EVENT, handleReconnect);
    };
  }, [sessionId, fetchWithAuth, mapMessage, setMessages, handleHistoricalTailMessages]);

  // Periodically reread the current page while visible and idle. This catches
  // canonical-row updates (notably remotely resolved HITL rows) that an
  // `after` cursor cannot see because their created_at value is unchanged.
  useEffect(() => {
    if (!hasLoadedMessages) return;

    let cancelled = false;
    const controller = new AbortController();
    const capturedSessionId = sessionId;

    function tick() {
      if (cancelled) return;
      if (sessionIdRef.current !== capturedSessionId) return;
      if (statusRef.current === "submitted" || statusRef.current === "streaming") return;
      if (prependingRef.current || isPrependingRef.current) return;
      if (document.visibilityState !== "visible") return;

      fetchWithAuth(
        `/api/sessions/${capturedSessionId}/messages?limit=${POLL_PAGE_SIZE}&complete_turns=true`,
        { headers: {}, signal: controller.signal },
      )
        .then((res) => (res.ok ? res.json() as Promise<SessionMessageListResponse> : null))
        .then((data) => {
          if (
            !data
            || cancelled
            || sessionIdRef.current !== capturedSessionId
            || prependingRef.current
            || isPrependingRef.current
          ) return;
          const nextTotal = data.total;
          if (typeof nextTotal === "number" && nextTotal !== totalMessagesRef.current) {
            setTotalMessages(nextTotal);
          }
          if (messageCacheEnabled()) void writeCache("messageTails", "tail", capturedSessionId, { messages: messageTailPreview(data.messages), has_more: data.has_more === true, total: data.total ?? null });
          const incoming = data.messages.map(mapMessage);
          if (incoming.length > 0) {
            if (historicalWindowRef.current) {
              handleHistoricalTailMessages(incoming);
            } else {
              const current = messagesRef.current;
              const hasOptimistic = current.some((message) => !isCanonical(message));
              const options = hasOptimistic ? { dropOptimistic: true } : undefined;
              // A no-op merge must skip setState itself. Keep the updater for
              // real changes so a concurrent feed update is merged, not lost.
              if (mergeMessages(current, incoming, options) !== current) {
                setMessages((previous) => {
                  if (
                    cancelled
                    || sessionIdRef.current !== capturedSessionId
                    || prependingRef.current
                    || isPrependingRef.current
                  ) return previous;
                  return mergeMessages(
                    previous,
                    incoming,
                    previous.some((message) => !isCanonical(message))
                      ? { dropOptimistic: true }
                      : undefined,
                  );
                });
              }
            }
          }
          const unread = notificationState.get(capturedSessionId);
          if (
            hasUserInteracted
            && loadedSessionId === capturedSessionId
            && messagesRef.current.length > 0
            && unread?.hasUnread
            && unread.runStatus != null
            && ["completed", "failed", "cancelled"].includes(unread.runStatus)
            && statusRef.current !== "submitted"
            && statusRef.current !== "streaming"
            && !cancelled
            && sessionIdRef.current === capturedSessionId
          ) {
            markSessionRead(capturedSessionId);
            void markInboxReadBySession?.(capturedSessionId);
          }
        })
        .catch((error) => {
          if (controller.signal.aborted || (error instanceof DOMException && error.name === "AbortError")) return;
          console.error("[ui] Periodic poll failed:", error);
        });
    }

    const intervalId = setInterval(tick, pollIntervalMs);
    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(intervalId);
    };
  }, [
    sessionId,
    hasLoadedMessages,
    fetchWithAuth,
    mapMessage,
    pollIntervalMs,
    setMessages,
    handleHistoricalTailMessages,
    notificationState,
    hasUserInteracted,
    loadedSessionId,
    markSessionRead,
    markInboxReadBySession,
  ]);

  const loadNavigationTarget = useCallback(async (
    target: ConversationTarget,
    signal: AbortSignal,
  ): Promise<NavigationTargetLoadResult> => {
    const capturedSessionId = sessionId;
    const requestGeneration = ++navigationRequestGenerationRef.current;
    activePrependControllerRef.current?.abort();
    navigationNewerControllerRef.current?.abort();
    navigationNewerControllerRef.current = null;
    setIsLoadingNewerNavigation(false);
    setOlderPageError(null);
    setNewerPageError(null);
    const ownsRequest = () => (
      !signal.aborted
      && sessionIdRef.current === capturedSessionId
      && navigationRequestGenerationRef.current === requestGeneration
    );
    let probe = capabilityCacheRef.current;
    if (
      !probe
      || probe.sessionId !== capturedSessionId
      || probe.expiresAt <= Date.now()
    ) {
      const result = await probeNavigationCapabilities(
        fetchWithAuth,
        capturedSessionId,
        signal,
      );
      const ttl = result.state === "supported"
        ? 30_000
        : result.state === "unsupported"
          ? 10_000
          : 0;
      probe = { sessionId: capturedSessionId, expiresAt: Date.now() + ttl, result };
      capabilityCacheRef.current = ttl > 0 ? probe : null;
    }
    if (!ownsRequest()) return { state: "error" };
    if (probe.result.state === "unsupported") return { state: "unsupported" };
    if (probe.result.state === "denied") return { state: "denied" };
    if (probe.result.state === "error") return { state: "error" };
    if (!probe.result.capabilities.target_window) return { state: "unsupported" };

    try {
      const windowResponse = await fetchNavigationWindow(
        fetchWithAuth,
        capturedSessionId,
        target,
        signal,
      );
      if (!ownsRequest()) return { state: "error" };
      const assembledSlots: Array<SessionMessageResponse | null> = [];
      for (const row of windowResponse.messages) {
        if (!isOversizedNavigationMessage(row)) {
          assembledSlots.push(row);
          continue;
        }
        // Non-target oversized context remains an explicit coverage gap. Only
        // the selected message is assembled, and cancellation is checked on
        // every bounded chunk.
        if (row.message_id !== windowResponse.target.message_id) {
          assembledSlots.push(null);
          continue;
        }
        let content = "";
        let cursor: string | null = row.content_cursor;
        let complete = false;
        while (cursor && ownsRequest()) {
          const chunk = await fetchNavigationContentChunk(
            fetchWithAuth,
            capturedSessionId,
            row.message_id,
            cursor,
            signal,
          );
          if (chunk.revision !== row.revision) throw new Error("navigation_content_changed");
          content += chunk.text;
          cursor = chunk.next_cursor;
          complete = chunk.complete;
          if (target.kind === "message" && target.headingKey) {
            const headings = await extractConversationHeadings(content);
            if (headings.some((heading) => heading.key === target.headingKey)) break;
          } else {
            break;
          }
        }
        if (!ownsRequest()) return { state: "error" };
        assembledSlots.push({
          id: row.message_id,
          session_id: row.session_id,
          run_id: row.run_id,
          role: row.role,
          content,
          status: row.status,
          created_at: row.created_at,
          metadata: {
            ...row.metadata,
            navigation_partial_content: !complete,
            navigation_content_cursor: cursor,
            navigation_content_revision: row.revision,
          },
          navigation_turn_id: row.navigation_turn_id,
        });
      }
      const targetSlot = assembledSlots.findIndex(
        (row) => row?.id === windowResponse.target.message_id,
      );
      if (targetSlot < 0) return { state: "missing" };
      let contiguousStart = targetSlot;
      while (contiguousStart > 0 && assembledSlots[contiguousStart - 1] !== null) {
        contiguousStart -= 1;
      }
      let contiguousEnd = targetSlot + 1;
      while (
        contiguousEnd < assembledSlots.length
        && assembledSlots[contiguousEnd] !== null
      ) {
        contiguousEnd += 1;
      }
      const gapBefore = contiguousStart > 0;
      const gapAfter = contiguousEnd < assembledSlots.length;
      const assembled = assembledSlots
        .slice(contiguousStart, contiguousEnd)
        .filter((row): row is SessionMessageResponse => row !== null);
      const mapped = assembled.map(mapMessage);
      if (!mapped.some((message) => message.id === windowResponse.target.message_id)) {
        return { state: "missing" };
      }
      const baseCoverage = mapWindowCoverage(windowResponse.coverage);
      const coverage: MessageWindowCoverage = {
        ...baseCoverage,
        firstMessageId: mapped[0]?.id ?? baseCoverage.firstMessageId,
        lastMessageId: mapped.at(-1)?.id ?? baseCoverage.lastMessageId,
        hasOlder: baseCoverage.hasOlder || gapBefore,
        hasNewer: baseCoverage.hasNewer || gapAfter,
        beforeCursor: gapBefore ? null : baseCoverage.beforeCursor,
        afterCursor: gapAfter ? null : baseCoverage.afterCursor,
        startsAtTurnBoundary: !gapBefore && baseCoverage.startsAtTurnBoundary,
        endsAtTurnBoundary: !gapAfter && baseCoverage.endsAtTurnBoundary,
        partialTurn: baseCoverage.partialTurn || gapBefore || gapAfter,
      };
      if (!historicalWindowRef.current) {
        messageWindowStoreRef.current = {
          ...messageWindowStoreRef.current,
          tail: messagesRef.current,
        };
      }
      const key = `${target.kind}:${JSON.stringify(target)}`;
      messageWindowStoreRef.current = activateMessageWindow(
        messageWindowStoreRef.current,
        {
          key,
          messages: mapped,
          coverage,
          generation: windowResponse.generation,
          requestGeneration,
          lastUsed: Date.now(),
        },
      );
      historicalWindowRef.current = true;
      setIsHistoricalWindow(true);
      setHasNewerWindowActivity(messageWindowStoreRef.current.newTailActivity);
      setHasMoreMessages(coverage.hasOlder && coverage.beforeCursor !== null);
      setTotalMessages(null);
      setNavigationCoverage(coverage);
      const optimistic = messagesRef.current.filter((message) => !isCanonical(message));
      setMessages(mergeMessages(mapped, optimistic));
      return { state: "loaded", messageId: windowResponse.target.message_id };
    } catch (error) {
      if (signal.aborted || (error instanceof DOMException && error.name === "AbortError")) {
        throw error;
      }
      const statusCode = typeof error === "object" && error !== null && "status" in error
        ? Number((error as { status?: unknown }).status)
        : undefined;
      if (statusCode === 401 || statusCode === 403) return { state: "denied" };
      if (statusCode === 404) return { state: "missing" };
      if ([405, 501].includes(statusCode ?? 0)) {
        capabilityCacheRef.current = null;
        return { state: "unsupported" };
      }
      return { state: "error" };
    }
  }, [fetchWithAuth, mapMessage, sessionId, setMessages]);

  const jumpToLatest = useCallback(() => {
    navigationRequestGenerationRef.current += 1;
    activePrependControllerRef.current?.abort();
    messageWindowStoreRef.current = selectLatestTail(messageWindowStoreRef.current);
    historicalWindowRef.current = false;
    setIsHistoricalWindow(false);
    setHasNewerWindowActivity(false);
    setNavigationCoverage(null);
    navigationContentControllerRef.current?.abort();
    navigationContentControllerRef.current = null;
    navigationNewerControllerRef.current?.abort();
    navigationNewerControllerRef.current = null;
    setNavigationContentMessageId(null);
    setNavigationContentError(false);
    setIsLoadingNewerNavigation(false);
    setOlderPageError(null);
    setNewerPageError(null);
    setMessages(messageWindowStoreRef.current.tail);
    setMessageLoadRetryNonce((nonce) => nonce + 1);
    if (typeof window !== "undefined") {
      pushConversationDestination({ sessionId });
    }
  }, [sessionId, setMessages]);
  restoreLatestBeforeSendRef.current = jumpToLatest;

  const loadOlderHistoricalWindow = useCallback(async ({
    force = false,
  }: LoadMoreMessagesOptions = {}) => {
    const activeKey = messageWindowStoreRef.current.activeWindowKey;
    const activeWindow = activeKey
      ? messageWindowStoreRef.current.windows.get(activeKey)
      : null;
    const cursor = activeWindow?.coverage.beforeCursor;
    if (!activeKey || !activeWindow || !cursor) return;
    if (!force && (isLoadingMoreRef.current || isPrependingRef.current)) return;
    if (force) activePrependControllerRef.current?.abort();

    const capturedSessionId = sessionId;
    const controller = new AbortController();
    const requestGeneration = ++prependRequestGenerationRef.current;
    activePrependControllerRef.current = controller;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    setOlderPageError(null);
    prependingRef.current = true;
    let committedPrepend = false;
    const ownsCurrentRequest = () => (
      !controller.signal.aborted
      && historicalWindowRef.current
      && messageWindowStoreRef.current.activeWindowKey === activeKey
      && sessionIdRef.current === capturedSessionId
      && activePrependControllerRef.current === controller
      && prependRequestGenerationRef.current === requestGeneration
    );
    try {
      const page = await fetchNavigationWindowPage(
        fetchWithAuth,
        capturedSessionId,
        "before",
        cursor,
        controller.signal,
      );
      if (!ownsCurrentRequest()) return;
      if (page.generation !== activeWindow.generation) throw new Error("navigation_generation_changed");
      let contiguousStart = 0;
      page.messages.forEach((row, index) => {
        if (isOversizedNavigationMessage(row)) contiguousStart = index + 1;
      });
      const hasContentGap = contiguousStart > 0;
      const older = page.messages
        .slice(contiguousStart)
        .filter((row): row is SessionMessageResponse => !isOversizedNavigationMessage(row))
        .map(mapMessage);
      const rawPageCoverage = mapWindowCoverage(page.coverage);
      const pageCoverage: MessageWindowCoverage = hasContentGap
        ? {
            ...rawPageCoverage,
            firstMessageId: older[0]?.id ?? rawPageCoverage.firstMessageId,
            hasOlder: true,
            beforeCursor: null,
            startsAtTurnBoundary: false,
            partialTurn: true,
          }
        : rawPageCoverage;
      if (older.length === 0) {
        setHasMoreMessages(false);
        setNavigationCoverage((current) => current ? {
          ...current,
          hasOlder: hasContentGap,
          beforeCursor: null,
          startsAtTurnBoundary: false,
          partialTurn: true,
        } : current);
        return;
      }
      await queueMessageMutation("prepend", (messageMutationRevision) => {
        if (!ownsCurrentRequest()) return false;
        messageWindowStoreRef.current = mergeAdjacentWindow(
          messageWindowStoreRef.current,
          activeKey,
          older,
          pageCoverage,
          "before",
          page.generation,
        );
        const revision = prependRevisionRef.current + 1;
        prependRevisionRef.current = revision;
        const existingIds = new Set(messagesRef.current.map((message) => message.id));
        pendingPrependCommitRef.current = {
          sessionId: capturedSessionId,
          revision,
          expectedMessageId: older.find((message) => !existingIds.has(message.id))?.id ?? null,
          messageMutationRevision,
        };
        isPrependingRef.current = true;
        setIsPrepending(true);
        setMessages((previous) => mergeMessages(previous, older));
        setPrependRevision(revision);
        setHasMoreMessages(pageCoverage.hasOlder && pageCoverage.beforeCursor !== null);
        setNavigationCoverage(messageWindowStoreRef.current.windows.get(activeKey)?.coverage ?? pageCoverage);
        committedPrepend = true;
        return true;
      });
    } catch (error) {
      if (ownsCurrentRequest()) reportPageError("older", error);
    } finally {
      if (activePrependControllerRef.current !== controller) return;
      activePrependControllerRef.current = null;
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
      prependingRef.current = false;
      if (!committedPrepend) {
        pendingPrependCommitRef.current = null;
        isPrependingRef.current = false;
        setIsPrepending(false);
      }
    }
  }, [fetchWithAuth, mapMessage, queueMessageMutation, reportPageError, sessionId, setMessages]);

  // The scroll controller decides when geometry requires older rows; this hook
  // owns only fetching, merging, and the exact prepend transaction boundary.
  const loadMoreMessages = useCallback(async ({
    force = false,
  }: LoadMoreMessagesOptions = {}) => {
    if (historicalWindowRef.current) {
      await loadOlderHistoricalWindow({ force });
      return;
    }
    const capturedSessionId = sessionId;
    const beforeId = selectOldestCanonicalId(messagesRef.current);
    if (
      !beforeId
      // Recovery polling has a serialized tail path (above), so it can load
      // history concurrently. A live SSE stream still mutates the tail outside
      // that queue and therefore retains the conservative block.
      || hasUnserializedMessageStream(
        statusRef.current,
        isPollingRef.current,
      )
      || sessionIdRef.current !== capturedSessionId
      || (!force && (
        isLoadingMoreRef.current
        || isPrependingRef.current
        || prependingRef.current
      ))
    ) return;

    if (force) {
      // The manual control is an escape hatch for a request that never
      // completed cleanly. Supersede it instead of waiting indefinitely, and
      // discard any incomplete shift transaction before fetching a fresh page.
      activePrependControllerRef.current?.abort();
      pendingPrependCommitRef.current = null;
      isPrependingRef.current = false;
      setIsPrepending(false);
      const pendingMutation = pendingMessageMutationRef.current;
      if (pendingMutation?.kind === "prepend") {
        pendingMessageMutationRef.current = null;
        pendingMutation.resolve();
      }
    }

    const controller = new AbortController();
    const requestGeneration = ++prependRequestGenerationRef.current;
    activePrependControllerRef.current = controller;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    setOlderPageError(null);
    prependingRef.current = true;
    let committedPrepend = false;
    const ownsCurrentRequest = () => (
      !controller.signal.aborted
      && sessionIdRef.current === capturedSessionId
      && prependRequestGenerationRef.current === requestGeneration
      && activePrependControllerRef.current === controller
    );

    try {
      const res = await fetchWithAuth(
        `/api/sessions/${capturedSessionId}/messages?before=${beforeId}&limit=${PAGE_SIZE}&complete_turns=true`,
        { headers: {}, signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15_000)]) },
      );
      if (!ownsCurrentRequest()) return;
      if (!res.ok) throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });
      const data = await res.json() as SessionMessageListResponse;
      if (!ownsCurrentRequest()) return;
      // The request may have started during recovery polling and then raced a
      // fresh user submission. Keep live SSE on its original conservative
      // path; only the polling transport shares the prepend/tail queue.
      if (hasUnserializedMessageStream(statusRef.current, isPollingRef.current)) {
        return;
      }
      const older = data.messages.map(mapMessage);

      if (older.length === 0) {
        if (ownsCurrentRequest()) {
          setHasMoreMessages(false);
        }
        return;
      }

      await queueMessageMutation("prepend", (messageMutationRevision) => {
        if (!ownsCurrentRequest()) return false;

        hasLoadedOlderRef.current = true;
        const revision = prependRevisionRef.current + 1;
        prependRevisionRef.current = revision;
        const existingIds = new Set(messagesRef.current.map((message) => message.id));
        const expectedMessageId = older.find((message) => !existingIds.has(message.id))?.id ?? null;
        pendingPrependCommitRef.current = {
          sessionId: capturedSessionId,
          revision,
          expectedMessageId,
          messageMutationRevision,
        };
        // Keep this state update adjacent to setMessages so consumers observe
        // the prepend signal in the same React commit as the older rows. The
        // mutation queue holds recovery-poll tail merges until Virtua has
        // applied this exact start-only shift transaction.
        isPrependingRef.current = true;
        setIsPrepending(true);
        setMessages((previous) => (
          controller.signal.aborted
            || sessionIdRef.current !== capturedSessionId
            || prependRequestGenerationRef.current !== requestGeneration
            ? previous
            : mergeMessages(previous, older)
        ));
        setPrependRevision(revision);
        setHasMoreMessages(data.has_more ?? false);
        committedPrepend = true;
        return true;
      });
    } catch (error) {
      if (ownsCurrentRequest()) reportPageError("older", error);
    } finally {
      // A forced request can replace this operation while its fetch promise is
      // unwinding. Only the current owner may clear shared pagination state.
      if (activePrependControllerRef.current !== controller) return;

      activePrependControllerRef.current = null;
      if (sessionIdRef.current !== capturedSessionId) return;
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
      prependingRef.current = false;
      // A successful prepend is cleared by the post-commit layout effect.
      // Failed/empty requests never set the flag, so they can clear it here.
      if (!committedPrepend) {
        pendingPrependCommitRef.current = null;
        isPrependingRef.current = false;
        setIsPrepending(false);
      }
    }
  }, [sessionId, fetchWithAuth, mapMessage, queueMessageMutation, setMessages, loadOlderHistoricalWindow, reportPageError]);

  const cancelNavigationContent = useCallback(() => {
    navigationContentControllerRef.current?.abort();
    navigationContentControllerRef.current = null;
    setNavigationContentMessageId(null);
  }, []);

  const loadNewerNavigationMessages = useCallback(async () => {
    const activeKey = messageWindowStoreRef.current.activeWindowKey;
    const active = activeKey ? messageWindowStoreRef.current.windows.get(activeKey) : null;
    const cursor = active?.coverage.afterCursor;
    if (!activeKey || !active || !active.coverage.hasNewer || !cursor || navigationNewerControllerRef.current) return;
    const controller = new AbortController();
    navigationNewerControllerRef.current = controller;
    setIsLoadingNewerNavigation(true);
    setNewerPageError(null);
    const requestGeneration = navigationRequestGenerationRef.current;
    const ownsCurrentRequest = () => (
      !controller.signal.aborted
      && sessionIdRef.current === sessionId
      && navigationRequestGenerationRef.current === requestGeneration
      && messageWindowStoreRef.current.activeWindowKey === activeKey
    );
    try {
      const page = await fetchNavigationWindowPage(
        fetchWithAuth,
        sessionId,
        "after",
        cursor,
        controller.signal,
      );
      if (!ownsCurrentRequest()) return;
      if (page.generation !== active.generation) throw new Error("navigation_generation_changed");
      const firstContentGap = page.messages.findIndex(isOversizedNavigationMessage);
      const hasContentGap = firstContentGap >= 0;
      const contiguousRows = hasContentGap
        ? page.messages.slice(0, firstContentGap)
        : page.messages;
      const newer = contiguousRows
        .filter((row): row is SessionMessageResponse => !isOversizedNavigationMessage(row))
        .map(mapMessage);
      const rawCoverage = mapWindowCoverage(page.coverage);
      const coverage: MessageWindowCoverage = hasContentGap
        ? {
            ...rawCoverage,
            lastMessageId: newer.at(-1)?.id ?? rawCoverage.lastMessageId,
            hasNewer: true,
            afterCursor: null,
            endsAtTurnBoundary: false,
            partialTurn: true,
          }
        : rawCoverage;
      await queueMessageMutation("poll-tail", () => {
        if (!ownsCurrentRequest()) return false;
        messageWindowStoreRef.current = mergeAdjacentWindow(
          messageWindowStoreRef.current,
          activeKey,
          newer,
          coverage,
          "after",
          page.generation,
        );
        const merged = messageWindowStoreRef.current.windows.get(activeKey);
        if (!merged) return false;
        setNavigationCoverage(merged.coverage);
        setMessages(merged.messages);
        return true;
      });
    } catch (error) {
      if (ownsCurrentRequest()) reportPageError("newer", error);
    } finally {
      if (navigationNewerControllerRef.current === controller) {
        navigationNewerControllerRef.current = null;
        setIsLoadingNewerNavigation(false);
      }
    }
  }, [fetchWithAuth, mapMessage, queueMessageMutation, reportPageError, sessionId, setMessages]);

  const continueNavigationContent = useCallback(async (messageId: string) => {
    const message = messagesRef.current.find((candidate) => candidate.id === messageId);
    const cursor = message?.metadata?.navigationContentCursor;
    const revision = message?.metadata?.navigationContentRevision;
    if (typeof cursor !== "string" || typeof revision !== "string") return;

    navigationContentControllerRef.current?.abort();
    const controller = new AbortController();
    navigationContentControllerRef.current = controller;
    setNavigationContentMessageId(messageId);
    setNavigationContentError(false);
    try {
      const chunk = await fetchNavigationContentChunk(
        fetchWithAuth,
        sessionId,
        messageId,
        cursor,
        controller.signal,
      );
      if (chunk.revision !== revision) throw new Error("navigation_content_changed");
      if (controller.signal.aborted || sessionIdRef.current !== sessionId) return;
      const extend = (source: AgnoChatMessage[]) => source.map((candidate) => {
        if (candidate.id !== messageId) return candidate;
        const content = `${candidate.content ?? ""}${chunk.text}`;
        return {
          ...candidate,
          content,
          parts: [{ type: "text" as const, text: content }],
          metadata: {
            ...candidate.metadata,
            navigationPartialContent: !chunk.complete,
            navigationContentCursor: chunk.next_cursor,
            navigationContentRevision: chunk.revision,
          },
        };
      });
      const activeKey = messageWindowStoreRef.current.activeWindowKey;
      if (activeKey) {
        const active = messageWindowStoreRef.current.windows.get(activeKey);
        if (active) {
          const windows = new Map(messageWindowStoreRef.current.windows);
          windows.set(activeKey, { ...active, messages: extend(active.messages), lastUsed: Date.now() });
          messageWindowStoreRef.current = { ...messageWindowStoreRef.current, windows };
        }
      }
      setMessages((previous) => extend(previous));
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error("[ui] Failed to continue oversized navigation content:", error);
        setNavigationContentError(true);
      }
    } finally {
      if (navigationContentControllerRef.current === controller) {
        navigationContentControllerRef.current = null;
        setNavigationContentMessageId(null);
      }
    }
  }, [fetchWithAuth, sessionId, setMessages]);

  return {
    messages,
    sendMessage,
    status,
    isPolling,
    stop,
    recoverableFailedRun,
    continueFromFailedRun,
    isUploading,
    isTranscribing,
    continueRun,
    hasMoreMessages,
    totalMessages,
    isLoadingMore,
    loadMoreMessages,
    olderPageError,
    newerPageError,
    isPrepending,
    prependRevision,
    hasLoadedMessages,
    messageLoadError,
    handleRetryMessageLoad,
    loadNavigationTarget,
    isHistoricalWindow,
    hasNewerWindowActivity,
    jumpToLatest,
    navigationCoverage,
    navigationContentMessageId,
    navigationContentError,
    continueNavigationContent,
    cancelNavigationContent,
    isLoadingNewerNavigation,
    loadNewerNavigationMessages,
  };
}
